import { z } from 'zod';
/**
 * Zod schemas for the Support endpoints. Channel-config validation is keyed
 * on `kind` so we get a discriminated union and exhaustive coverage.
 */
const TelegramConfig = z.object({
    kind: z.literal('telegram'),
    botToken: z.string().trim().min(20).max(512),
    botUsername: z.string().trim().min(1).max(128).optional(),
});
const FacebookConfig = z.object({
    kind: z.literal('facebook'),
    pageId: z.string().trim().min(1).max(128),
    pageAccessToken: z.string().trim().min(20).max(2048),
    pageName: z.string().trim().min(1).max(256).optional(),
});
const InstagramConfig = z.object({
    kind: z.literal('instagram'),
    igUserId: z.string().trim().min(1).max(128),
    pageId: z.string().trim().min(1).max(128),
    pageAccessToken: z.string().trim().min(20).max(2048),
});
const WhatsappConfig = z.object({
    kind: z.literal('whatsapp'),
    phoneNumberId: z.string().trim().min(1).max(128),
    wabaId: z.string().trim().min(1).max(128),
    accessToken: z.string().trim().min(20).max(2048),
    displayPhoneNumber: z.string().trim().min(1).max(64).optional(),
});
const EmailImapConfig = z.object({
    kind: z.literal('email_imap'),
    email: z.string().email().trim().max(320),
    imapHost: z.string().trim().min(1).max(256),
    imapPort: z.number().int().positive().max(65535),
    smtpHost: z.string().trim().min(1).max(256),
    smtpPort: z.number().int().positive().max(65535),
    appPassword: z.string().min(4).max(512),
    useTls: z.boolean().optional(),
});
export const SupportChannelConfigSchema = z.discriminatedUnion('kind', [
    TelegramConfig,
    FacebookConfig,
    InstagramConfig,
    WhatsappConfig,
    EmailImapConfig,
]);
export const CreateSupportChannelRequestSchema = z.object({
    label: z.string().trim().min(1).max(120),
    config: SupportChannelConfigSchema,
});
/**
 * Edit-time config: the secret field of each kind is optional, and omitting it
 * keeps the secret already saved — secrets are never sent back to the browser,
 * so an edit form cannot resubmit them.
 */
export const SupportChannelConfigPatchSchema = z.discriminatedUnion('kind', [
    TelegramConfig.partial({ botToken: true }),
    FacebookConfig.partial({ pageAccessToken: true }),
    InstagramConfig.partial({ pageAccessToken: true }),
    WhatsappConfig.partial({ accessToken: true }),
    EmailImapConfig.partial({ appPassword: true }),
]);
export const UpdateSupportChannelRequestSchema = z
    .object({
    label: z.string().trim().min(1).max(120).optional(),
    isActive: z.boolean().optional(),
    aiEnabled: z.boolean().optional(),
    config: SupportChannelConfigPatchSchema.optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const SupportChannelIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const SupportConversationIdParamSchema = z.object({
    id: z.string().uuid(),
});
// v2.0.0 PR #C — systemPrompt / model / temperature / maxTokens dropped;
// owned by the Support AgentProfile (slug='support'). Kept: behavioural knobs.
export const PatchSupportAiConfigRequestSchema = z
    .object({
    topKChunks: z.number().int().min(1).max(50).optional(),
    greetingMessage: z.string().max(4000).nullable().optional(),
    fallbackMessage: z.string().max(4000).nullable().optional(),
    autoOpenTicket: z.boolean().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const GrantSupportUserBodySchema = z.object({
    userId: z.string().uuid(),
});
export const ReplySupportConversationRequestSchema = z.object({
    body: z.string().min(1).max(4000),
});
/** Inline internal note posted by a human agent into a conversation thread. */
export const CreateSupportInternalNoteRequestSchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
/** Open a new ticket on a conversation. All fields optional — sensible defaults. */
export const CreateSupportTicketRequestSchema = z
    .object({
    subject: z.string().trim().max(160).nullable().optional(),
    summary: z.string().max(4000).nullable().optional(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
    priorityId: z.string().uuid().optional(),
})
    .default({});
/**
 * Raise a ticket from the board rather than from inside a conversation.
 *
 * Either an existing thread or a name, never both and never neither: a ticket
 * row cannot exist without a conversation to hang from, and silently picking
 * one for the agent would file it against the wrong customer.
 */
/**
 * UST-3147 — one contact point on the customer card.
 *
 * An empty string is normalised to null so clearing a field in the UI
 * erases it rather than storing `""`, which would read as "set" to every
 * lookup. The lengths are generous: handles and addresses vary wildly and a
 * rejected paste is worse than a long row.
 */
const contactValue = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v === '' ? null : v));
const contactShape = {
    contactPhone: contactValue(40),
    contactEmail: contactValue(320),
    contactTelegram: contactValue(160),
    contactInstagram: contactValue(160),
    contactFacebook: contactValue(320),
};
/** Contact block on a brand-new customer, created alongside their ticket. */
export const SupportContactInputSchema = z.object(contactShape);
/**
 * Body for PATCH /support/conversations/:id/contact.
 *
 * A field left out is left alone, so the card can be saved one row at a
 * time; an explicit null clears it. At least one key must be present —
 * an empty body is a client bug, not a no-op worth a 200.
 */
export const UpdateSupportContactRequestSchema = z
    .object(contactShape)
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const CreateSupportTicketFromBoardRequestSchema = z
    .object({
    conversationId: z.string().uuid().optional(),
    customerName: z.string().trim().min(1).max(120).optional(),
    customerHandle: z.string().trim().max(160).nullable().optional(),
    contact: SupportContactInputSchema.optional(),
    subject: z.string().trim().min(1).max(160),
    summary: z.string().max(4000).nullable().optional(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
    priorityId: z.string().uuid().optional(),
    assignedUserId: z.string().uuid().nullable().optional(),
})
    .refine((d) => Boolean(d.conversationId) !== Boolean(d.customerName), {
    message: 'Provide either an existing customer or a new customer name, not both',
    path: ['customerName'],
});
/** Append a free-form note to an existing ticket. */
export const CreateSupportTicketNoteRequestSchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
export const UpdateSupportTicketRequestSchema = z
    .object({
    subject: z.string().trim().min(1).max(160).optional(),
    summary: z.string().max(4000).nullable().optional(),
    status: z
        .enum(['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'])
        .optional(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
    priorityId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const SupportTicketIdParamSchema = z.object({
    id: z.string().uuid(),
    ticketId: z.string().uuid(),
});
/** Just the ticket id (used by /support/tickets/:ticketId routes). */
export const SupportTicketOnlyIdParamSchema = z.object({
    ticketId: z.string().uuid(),
});
/** UST-3195 — /support/tickets/:ticketId/files/:fileId. */
export const SupportTicketFileParamSchema = z.object({
    ticketId: z.string().uuid(),
    fileId: z.string().uuid(),
});
/** UST-3195 — PUT /support/tickets/:ticketId/files/:fileId: attach, and pick its "Files" field. */
export const AttachSupportTicketFileSchema = z
    .object({ fieldId: z.string().uuid().nullable().optional() })
    .default({});
/** UST-3195 — POST /support/tickets/:ticketId/files?fieldId=… */
export const UploadSupportTicketFileQuerySchema = z.object({
    fieldId: z.string().uuid().optional(),
});
/** UST-3195 — GET /support/files/:fileId. */
export const SupportFileIdParamSchema = z.object({
    fileId: z.string().uuid(),
});
/** Policy §5 — PUT /support/tickets/:ticketId/previous. Null clears the link. */
export const SetSupportTicketPreviousSchema = z.object({
    previousTicketId: z.string().uuid().nullable(),
});
/** UST-3143 — `/support/archive/deleted/:id`. */
export const SupportDeletedTicketIdParamSchema = z.object({
    id: z.string().uuid(),
});
/** UST-3143 — query for both Archive lists. */
export const SupportArchiveQuerySchema = z.object({
    search: z.string().trim().min(1).max(120).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
// ---------------------------------------------------------------------------
// Ticket board (UST-3127)
// ---------------------------------------------------------------------------
const TICKET_STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'];
/** Query for GET /support/tickets. Every filter is optional and they compose with AND. */
export const SupportTicketsQuerySchema = z.object({
    status: z.enum(TICKET_STATUSES).optional(),
    priority: z.enum(TICKET_PRIORITIES).optional(),
    /** A priority from the Priority policy. */
    priorityId: z.string().uuid().optional(),
    /** A user id, or the literals 'me' / 'unassigned'. */
    assignee: z.union([z.literal('me'), z.literal('unassigned'), z.string().uuid()]).optional(),
    channelKind: z
        .enum(['telegram', 'facebook', 'instagram', 'whatsapp', 'email_imap', 'phone', 'manual'])
        .optional(),
    /** Who opened it — the AI agent or a person. */
    source: z.enum(['ai_agent', 'human_agent']).optional(),
    search: z.string().trim().min(1).max(120).optional(),
    limit: z.coerce.number().int().min(1).max(1000).optional(),
    /**
     * UST-3143 — the Odoo Helpdesk board for one team. Without it the board is
     * Support Tickets, which leaves out every ticket sent to Odoo.
     */
    odooTeamId: z.coerce.number().int().positive().optional(),
    /** `pending` — tickets waiting for account-manager approval. */
    approval: z.literal('pending').optional(),
});
/** Body for PATCH /support/tickets/:ticketId. Same fields as the conversation-scoped update, plus assignment. */
export const PatchSupportTicketRequestSchema = z
    .object({
    subject: z.string().trim().min(1).max(160).optional(),
    summary: z.string().max(4000).nullable().optional(),
    status: z.enum(TICKET_STATUSES).optional(),
    priority: z.enum(TICKET_PRIORITIES).optional(),
    priorityId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional(),
    assignedUserId: z.string().uuid().nullable().optional(),
    /** UST-3143 — move an Odoo Helpdesk ticket to one of its team's stages. */
    odooStageId: z.number().int().positive().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
/** Body for POST /support/tickets/:ticketId/drop. */
export const DropSupportTicketRequestSchema = z.object({
    reason: z.string().trim().min(1).max(2000),
});
export const SupportConversationsQuerySchema = z.object({
    channelId: z.string().uuid().optional(),
    kind: z
        .enum(['telegram', 'facebook', 'instagram', 'whatsapp', 'email_imap', 'phone', 'manual'])
        .optional(),
    status: z.enum(['open', 'pending', 'resolved', 'closed']).optional(),
    search: z.string().trim().min(1).max(120).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
//# sourceMappingURL=support.schema.js.map